import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { PlatformChartGroupDto } from '@data/_generated-api/platform-api/models/platform-chart-group-dto';
import { HD_CHART_DRAG_TYPE, collectChartGroupSubtreeIds } from './hd-chart-group-tree.util';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';

interface HdGroupTreeNode {
  group: PlatformChartGroupDto;
  level: number;
  hasChildren: boolean;
}

/** Drop van een groep op een nieuwe parent; null = naar hoofdniveau ("Alle charts"). */
export interface HdChartGroupMove {
  group: PlatformChartGroupDto;
  newParentId: string | null;
}

/** Drop van een chart (tabelrij) op een groep: chart toevoegen aan die groep. */
export interface HdChartDropOnGroup {
  chartId: string;
  groupId: string;
}

/** Herorden-drop: nieuwe sibling-volgorde van het doel-niveau, evt. met parent-wissel. */
export interface HdChartGroupReorder {
  /** Alle sibling-ids van het doel-niveau in de nieuwe volgorde (incl. de gesleepte groep). */
  orderedGroupIds: string[];
  /** Gezet wanneer de gesleepte groep daarbij óók van parent wisselt (rand-drop op een ander niveau). */
  reparent?: { group: PlatformChartGroupDto; newParentId: string | null };
}

/** Zone binnen een rij tijdens een groep-drag: boven/onder = herordenen, midden = re-parent. */
type HdGroupDropPosition = 'before' | 'inside' | 'after';

/** Aandeel van de rijhoogte (boven- en onderkant) dat als reorder-zone telt. */
const REORDER_EDGE_RATIO = 0.28;

/**
 * Zijbalk-boom van chartgroepen: vaste "Alle charts"-rij + geneste groepen
 * (depth-first uit de platte DTO-lijst, alfabetisch per niveau — de backend
 * levert al naam-gesorteerd). Selectie = filter; hover-acties per node.
 * Herstructureren kan door een groep op een andere groep te slepen (wordt kind)
 * of op "Alle charts" (naar hoofdniveau).
 */
@Component({
  selector: 'hd-chart-group-tree',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './hd-chart-group-tree.component.html',
  styleUrl: './hd-chart-group-tree.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.drop-zone-hint]': 'dropZoneHint()' },
})
export class HdChartGroupTreeComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly groups = input.required<PlatformChartGroupDto[]>();
  readonly selectedGroupId = input<string | null>(null);
  /** Stippellijn-affordance rond de boom zolang er een chart-rij gesleept wordt. */
  readonly dropZoneHint = input(false);
  /** Groep die kort oplicht na een geslaagde drop (succes-cue vanuit de manager). */
  readonly flashGroupId = input<string | null>(null);
  /** null = "Alle charts". */
  readonly selectedGroupIdChange = output<string | null>();
  readonly editRequested = output<PlatformChartGroupDto>();
  readonly deleteRequested = output<PlatformChartGroupDto>();
  readonly moveRequested = output<HdChartGroupMove>();
  readonly chartDropped = output<HdChartDropOnGroup>();
  /** Herorden-drop op de randzones: nieuwe volgorde van het doel-niveau, evt. met parent-wissel. */
  readonly reorderRequested = output<HdChartGroupReorder>();

  /** Ingeklapte nodes; default leeg = alles open (verwachting bij kleine bomen). */
  private readonly collapsedIds = signal<ReadonlySet<string>>(new Set());

  /** Id van de groep die momenteel gesleept wordt; null = geen drag bezig. */
  protected readonly draggedGroupId = signal<string | null>(null);
  /** Groep-rij die als geldig droptarget onder de cursor ligt. */
  protected readonly dropTargetId = signal<string | null>(null);
  /** Zone binnen de droptarget-rij ('before'/'after' = herordenen, 'inside' = re-parent). */
  protected readonly dropPosition = signal<HdGroupDropPosition | null>(null);
  /** Of de "Alle charts"-rij als droptarget (naar hoofdniveau) actief is. */
  protected readonly rootDropActive = signal(false);

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      allCharts: translate('humanDesign.chartManager.groups.allCharts'),
      edit: translate('humanDesign.chartManager.groups.editGroup'),
      delete: translate('humanDesign.chartManager.groups.deleteGroup'),
    };
  });

  /** Depth-first weergavelijst; ingeklapte subtrees worden overgeslagen. */
  protected readonly nodes = computed<HdGroupTreeNode[]>(() => {
    const groups = this.groups();
    const collapsed = this.collapsedIds();
    const childrenByParent = new Map<string | null, PlatformChartGroupDto[]>();
    for (const group of groups) {
      const key = group.parentGroupId ?? null;
      const siblings = childrenByParent.get(key) ?? [];
      siblings.push(group);
      childrenByParent.set(key, siblings);
    }

    const result: HdGroupTreeNode[] = [];
    const visit = (parentId: string | null, level: number) => {
      for (const group of childrenByParent.get(parentId) ?? []) {
        const hasChildren = childrenByParent.has(group.id ?? '');
        result.push({ group, level, hasChildren });
        if (group.id && !collapsed.has(group.id)) {
          visit(group.id, level + 1);
        }
      }
    };
    visit(null, 0);
    return result;
  });

  protected isCollapsed(id: string | null | undefined): boolean {
    return !!id && this.collapsedIds().has(id);
  }

  protected toggleCollapse(id: string | null | undefined, event: Event): void {
    event.stopPropagation();
    if (!id) return;
    const next = new Set(this.collapsedIds());
    if (!next.delete(id)) next.add(id);
    this.collapsedIds.set(next);
  }

  protected select(groupId: string | null): void {
    if (groupId === this.selectedGroupId()) return;
    this.selectedGroupIdChange.emit(groupId);
  }

  // ── Drag & drop: parent wijzigen door slepen ──────────────────────────
  protected onDragStart(event: DragEvent, group: PlatformChartGroupDto): void {
    if (!group.id) return;
    this.draggedGroupId.set(group.id);
    // Firefox start de drag alleen met data; de payload zelf gebruiken we niet.
    event.dataTransfer?.setData('text/plain', group.id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  protected onDragEnd(): void {
    this.draggedGroupId.set(null);
    this.dropTargetId.set(null);
    this.dropPosition.set(null);
    this.rootDropActive.set(false);
  }

  /**
   * Geldig droptarget: niet op zichzelf of de eigen subtree (cycle) en niet op
   * de huidige parent (no-op). targetId null = de "Alle charts"-rij (hoofdniveau).
   */
  protected canDropOn(targetId: string | null): boolean {
    const draggedId = this.draggedGroupId();
    if (!draggedId) return false;
    const dragged = this.groups().find(group => group.id === draggedId);
    if (!dragged) return false;
    if (targetId === null) return dragged.parentGroupId != null;
    if (targetId === (dragged.parentGroupId ?? null)) return false;
    return !collectChartGroupSubtreeIds(this.groups(), draggedId).has(targetId);
  }

  /** Sleept de manager een tabelrij (chart) over de boom? Tijdens dragover is alleen het type leesbaar. */
  private isChartDrag(event: DragEvent): boolean {
    return !!event.dataTransfer?.types.includes(HD_CHART_DRAG_TYPE);
  }

  protected onDragOver(event: DragEvent, targetId: string | null): void {
    if (this.isChartDrag(event)) {
      // Chart toevoegen kan aan elke groep; "Alle charts" is geen zinvol doel.
      if (targetId === null) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      this.dropTargetId.set(targetId);
      this.dropPosition.set('inside');
      return;
    }

    // Groep-drag: rand-zones van een sibling-rij herordenen, het midden re-parent.
    const position = targetId === null ? 'inside' : this.resolveDropPosition(event, targetId);
    const valid = position === 'inside' ? this.canDropOn(targetId) : this.canReorderAgainst(targetId);
    if (!valid) {
      if (this.dropTargetId() === targetId) this.dropPosition.set(null);
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    if (targetId === null) {
      this.rootDropActive.set(true);
    } else {
      this.dropTargetId.set(targetId);
      this.dropPosition.set(position);
    }
  }

  /** Zone binnen de rij op basis van de cursorhoogte; zonder element-context telt alles als midden. */
  private resolveDropPosition(event: DragEvent, targetId: string): HdGroupDropPosition {
    const row = event.currentTarget as HTMLElement | null;
    const rect = row?.getBoundingClientRect?.();
    if (!rect || rect.height <= 0) return 'inside';
    const ratio = (event.clientY - rect.top) / rect.height;
    if (ratio < REORDER_EDGE_RATIO) return 'before';
    if (ratio > 1 - REORDER_EDGE_RATIO) return 'after';
    return 'inside';
  }

  /**
   * Randzone-drop tegen een willekeurige groep: de gesleepte groep komt vóór/na
   * de target op het niveau van díé target — óók een ander niveau (incl. root),
   * dan wisselt hij mee van parent. Alleen de eigen subtree is verboden (cycle).
   */
  protected canReorderAgainst(targetId: string | null): boolean {
    const draggedId = this.draggedGroupId();
    if (!draggedId || !targetId || targetId === draggedId) return false;
    const dragged = this.groups().find(group => group.id === draggedId);
    const target = this.groups().find(group => group.id === targetId);
    if (!dragged || !target) return false;

    const subtree = collectChartGroupSubtreeIds(this.groups(), draggedId);
    if (subtree.has(targetId)) return false;
    const newParentId = target.parentGroupId ?? null;
    return newParentId === null || !subtree.has(newParentId);
  }

  protected onDragLeave(event: DragEvent): void {
    // Alleen resetten wanneer de cursor de rij echt verlaat (niet bij het
    // passeren van kind-elementen zoals de naam of de count-badge).
    const row = event.currentTarget as HTMLElement;
    if (event.relatedTarget instanceof Node && row.contains(event.relatedTarget)) return;
    this.dropTargetId.set(null);
    this.rootDropActive.set(false);
  }

  protected onDrop(event: DragEvent, targetId: string | null): void {
    event.preventDefault();

    if (this.isChartDrag(event)) {
      const chartId = event.dataTransfer?.getData(HD_CHART_DRAG_TYPE);
      if (chartId && targetId) {
        this.chartDropped.emit({ chartId, groupId: targetId });
      }
      this.onDragEnd();
      return;
    }

    const draggedId = this.draggedGroupId();
    const dragged = this.groups().find(group => group.id === draggedId);
    const position = targetId === null ? 'inside' : this.resolveDropPosition(event, targetId);

    if (dragged && targetId && position !== 'inside' && this.canReorderAgainst(targetId)) {
      const target = this.groups().find(group => group.id === targetId);
      if (target) {
        const newParentId = target.parentGroupId ?? null;
        const payload: HdChartGroupReorder = {
          orderedGroupIds: this.buildReorderedSiblingIds(dragged, target, position),
        };
        if ((dragged.parentGroupId ?? null) !== newParentId) {
          payload.reparent = { group: dragged, newParentId };
        }
        this.reorderRequested.emit(payload);
      }
    } else if (dragged && this.canDropOn(targetId)) {
      this.moveRequested.emit({ group: dragged, newParentId: targetId });
    }
    this.onDragEnd();
  }

  /** Sibling-ids van het parent-niveau in de nieuwe volgorde (gesleepte groep herplaatst rond de target). */
  private buildReorderedSiblingIds(
    dragged: PlatformChartGroupDto,
    target: PlatformChartGroupDto,
    position: 'before' | 'after',
  ): string[] {
    const parentKey = target.parentGroupId ?? null;
    const siblingIds = this.groups()
      .filter(group => (group.parentGroupId ?? null) === parentKey && !!group.id)
      .map(group => group.id as string)
      .filter(id => id !== dragged.id);
    const insertAt = siblingIds.indexOf(target.id as string) + (position === 'after' ? 1 : 0);
    siblingIds.splice(insertAt, 0, dragged.id as string);
    return siblingIds;
  }
}
